import {
  Alert,
  Button,
  Checkbox,
  classNames,
  EllipsisText,
  Spinner,
  toast
} from '@cma/common'
import {
  PlatformClientContentPartsFragment,
  PlatformClientFolderPartsFragment
} from '@cma/generated/graphql'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'
import React, { MouseEvent, useEffect, useState } from 'react'
import { Table } from 'src/common/Table'
import { useContent } from './ContentContext'
import { ContentFileMenu } from './ContentFileMenu'
import { useDragContent } from './DragContentContext'
import { RenameInput } from './RenameInput'
import { ShareContentModal } from './share_modal/ShareContentModal'
import { useDestroyPlatformClientContents } from './useDestroyPlatformClientContents'
import { useDestroyPlatformClientFolders } from './useDestroyPlatformClientFolders'
import { useUpdatePlatformClientContents } from './useUpdatePlatformClientContents'
import { useUpdatePlatformClientFolder } from './useUpdatePlatformClientFolder'
import { getFileIcon, getShareableContentName } from './utils'

export function ContentTableRow({
  item,
  index
}: {
  item: PlatformClientContentPartsFragment | PlatformClientFolderPartsFragment
  index?: number
}) {
  const {
    selectedContents,
    isDeleting,
    currentFolder,
    renamingFileId,
    hasContentPermissions,
    toggleFileSelection,
    setIsRenaming,
    setRenamingFileId,
    setPreviewContent
  } = useContent()

  const { getDraggableFiles } = useDragContent()

  const { mutate: destroyContents, isLoading: isDestroyingContents } =
    useDestroyPlatformClientContents()
  const { mutate: destroyFolder, isLoading: isDestroyingFolder } =
    useDestroyPlatformClientFolders()
  const { mutate: updateFolder, isLoading: isUpdatingFolder } =
    useUpdatePlatformClientFolder()
  const { mutate: updateContents, isLoading: isUpdatingContents } =
    useUpdatePlatformClientContents()

  const [showSharingModal, setShowSharingModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isRenamingLocal, setIsRenamingLocal] = useState(false)
  const [localName, setLocalName] = useState(item?.displayName || 'Untitled')

  useEffect(() => {
    setIsRenamingLocal(renamingFileId === item.id)
  }, [renamingFileId, item.id])

  const isFile = item.__typename === 'PlatformClientContent'
  const isFolder = item.__typename === 'Folder'
  const isSelected = selectedContents.includes(item.id)
  const isItemDeleting = isSelected && isDeleting
  const FileIcon = getFileIcon(
    item.__typename,
    Boolean(item.contentShares?.length)
  )
  const sharedWith =
    item?.contentShares?.map((shared) => getShareableContentName(shared)) || []

  const droppable = useDroppable({
    id: `Folder-${item.id}`,
    disabled: !isFolder
  })

  const draggable = useDraggable({
    id: `PlatformClientContent-${item.id}`,
    disabled: !isFile || isRenamingLocal || !hasContentPermissions
  })

  const dragStyle: React.CSSProperties = isFile
    ? {
        transform: draggable.transform
          ? CSS.Translate.toString(draggable.transform)
          : undefined,
        transition: draggable.isDragging ? undefined : 'transform 200ms ease'
      }
    : {}

  const handleRename = (value: string) => {
    if (!value.trim()) return false
    setLocalName(value)

    if (isFolder) {
      updateFolder(
        { folderId: item.id, displayName: value },
        {
          onSuccess() {
            toast(`${value} renamed successfully`, {
              variant: 'success'
            })
          },
          onError(error) {
            toast(error.message || 'Failed to rename folder', {
              variant: 'error'
            })
          },
          onSettled() {
            setIsRenamingLocal(false)
            setIsRenaming(false)
            setRenamingFileId(null)
          }
        }
      )
    } else {
      updateContents(
        { ids: [item.id], displayName: value },
        {
          onSuccess() {
            toast(`${value} renamed successfully`, {
              variant: 'success'
            })
          },
          onError(error) {
            toast(error.message, { variant: 'error' })
          },
          onSettled() {
            setIsRenamingLocal(false)
            setIsRenaming(false)
            setRenamingFileId(null)
          }
        }
      )
    }

    return true
  }

  const handleOpenPreview = () => {
    if (isFile) {
      setPreviewContent(item as PlatformClientContentPartsFragment)
    }
  }

  const setRowRef = isFolder ? droppable.setNodeRef : draggable.setNodeRef

  return (
    <>
      <tr
        ref={setRowRef}
        {...(isFile ? draggable.attributes : {})}
        {...(isFile ? draggable.listeners : {})}
        className={clsx('relative !border-solid hover:bg-gray-50', {
          'z-20 cursor-grabbing bg-white opacity-95 shadow-xl':
            isFile && draggable.isDragging,
          'cursor-pointer':
            !(isFile && draggable.isDragging) && !isItemDeleting,
          'cursor-default': isFolder,
          'pointer-events-none opacity-50': isItemDeleting,
          'bg-red-50': isItemDeleting,
          'bg-brand-white ring-brand-primary': isFolder && droppable.isOver
        })}
        style={dragStyle}>
        <Table.Data padding="compact" align="center">
          <div className="flex items-center justify-center px-4">
            <Checkbox
              checked={isSelected}
              onChange={() => toggleFileSelection(item.id)}
            />
          </div>
        </Table.Data>
        <Table.Data padding="compact">
          <div
            className={classNames(
              'flex min-h-[52px] w-full max-w-[260px] items-center space-x-3',
              { relative: isFile }
            )}>
            <FileIcon
              className={classNames('h-[41px] w-[32px]', {
                'cursor-pointer': isFile
              })}
              onClick={(e: MouseEvent<SVGSVGElement>) => {
                if (!isFile) return
                e.stopPropagation()
                handleOpenPreview()
              }}
            />
            <div className="helix-body ml-1 flex flex-1 items-center overflow-hidden py-1 text-gray-900">
              <ContentDisplayName
                type={item.__typename}
                index={index}
                itemId={item.id}
                isRenamingLocal={isRenamingLocal}
                localName={localName}
                onRename={handleRename}
                onCancelRename={() => {
                  setIsRenamingLocal(false)
                  setIsRenaming(false)
                  setRenamingFileId(null)
                }}
                onOpenPreview={handleOpenPreview}
                isUpdating={isUpdatingContents || isUpdatingFolder}
              />
            </div>
            {isFile &&
              (draggable.isDragging || droppable.isOver) &&
              !!getDraggableFiles(item.id).length && (
                <div className="absolute -left-16 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary text-xs font-bold text-white">
                  {getDraggableFiles(item.id).length}
                </div>
              )}
          </div>
        </Table.Data>
        <Table.Data padding="compact">
          <div className="w-96 min-w-[210px] max-w-[300px] capitalize text-gray-600">
            {sharedWith.length ? (
              <EllipsisText maxWidth={300}>
                {sharedWith.join(', ')}
              </EllipsisText>
            ) : (
              <span className="helix-body">Not shared</span>
            )}
          </div>
        </Table.Data>
        <Table.Data padding="compact">
          {!currentFolder && (
            <Button
              disabled={!hasContentPermissions}
              type="button"
              size="xxs"
              variant="ghost-secondary"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                setShowSharingModal(true)
              }}>
              Share
            </Button>
          )}
        </Table.Data>
        <Table.Data padding="compact">
          <div className="group relative mx-2 flex items-center justify-center">
            {hasContentPermissions && (
              <ContentFileMenu
                id={item.id}
                disabled={selectedContents?.length > 1}
                type={item.__typename}
                onDelete={() => setShowDeleteModal(true)}
                onRename={() => {
                  setIsRenamingLocal(true)
                  setIsRenaming(true)
                }}
                onPreview={isFile ? handleOpenPreview : undefined}
              />
            )}
          </div>
        </Table.Data>
        {(isDestroyingContents && isFile) ||
        (isDestroyingFolder && isFolder) ||
        isItemDeleting ? (
          <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center gap-3 bg-white/80">
            <div className="h-3.5 w-3.5 text-gray-500">
              <Spinner />
            </div>
            <span className="helix-small--strong text-gray-500">
              Deleting...
            </span>
          </div>
        ) : null}
      </tr>
      {hasContentPermissions && (
        <ShareContentModal
          ids={[{ id: item.id, type: item.__typename }]}
          open={showSharingModal}
          onClose={() => setShowSharingModal(false)}
        />
      )}
      {hasContentPermissions && (
        <Alert variant="danger" isOpen={showDeleteModal}>
          <Alert.Title>Delete {isFolder ? 'Folder' : 'Files'}</Alert.Title>
          <Alert.Content>
            Are you sure you want to delete{' '}
            <strong className="font-medium text-gray-700">{localName}</strong>?
            This action cannot be undone.
          </Alert.Content>
          <Alert.Cancel onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Alert.Cancel>
          <Alert.Confirm
            loading={isFolder ? isDestroyingFolder : isDestroyingContents}
            onClick={() => {
              if (isFolder) {
                destroyFolder(
                  { ids: [item.id] },
                  {
                    onSuccess() {
                      toast(`${localName} deleted successfully`, {
                        variant: 'success'
                      })
                    },
                    onError(error) {
                      toast(error.message || 'Failed to delete folder', {
                        variant: 'error'
                      })
                    }
                  }
                )
              } else {
                destroyContents(
                  { ids: [item.id] },
                  {
                    onSuccess() {
                      toast(`${localName} deleted successfully`, {
                        variant: 'success'
                      })
                    },
                    onError(error) {
                      toast(error.message, { variant: 'error' })
                    }
                  }
                )
              }
              setShowDeleteModal(false)
            }}>
            Delete Forever
          </Alert.Confirm>
        </Alert>
      )}
    </>
  )
}

function ContentDisplayName({
  type,
  index,
  isRenamingLocal,
  localName,
  isUpdating,
  itemId,
  onRename,
  onCancelRename,
  onOpenPreview
}: {
  type: 'PlatformClientContent' | 'Folder'
  index?: number
  isRenamingLocal: boolean
  localName: string
  isUpdating: boolean
  itemId: string
  onRename: (val: string) => boolean
  onCancelRename: () => void
  onOpenPreview?: () => void
}) {
  const { navigateToFolder } = useContent()
  const { currentDraggingFile, getDraggableFiles } = useDragContent()

  const files = getDraggableFiles(itemId)
  const showFileCount =
    files.length > 1 && files[files.length - 1].id === currentDraggingFile

  if (isRenamingLocal) {
    return isUpdating ? (
      <div className="mt-2 flex min-h-[24px] items-center justify-center gap-2 px-1 pb-1">
        <span className="helix-body line-clamp-2 block w-full break-all text-gray-400">
          {localName}
        </span>
        <div className="helix-text-brand h-4 w-4">
          <Spinner />
        </div>
      </div>
    ) : (
      <RenameInput
        variant={type === 'PlatformClientContent' ? 'md' : 'sm'}
        defaultValue={localName}
        saveButtonPosition="inline"
        onSubmit={onRename}
        onCancel={onCancelRename}
      />
    )
  }

  if (type === 'Folder' && navigateToFolder) {
    return (
      <button
        type="button"
        className="flex cursor-pointer items-center break-all text-left text-gray-900 hover:text-brand-primary"
        onClick={() => navigateToFolder(itemId)}>
        {localName}
      </button>
    )
  }

  return (
    <button
      type="button"
      className="cursor-pointer break-all text-left hover:text-brand-primary"
      onClick={(e) => {
        e.stopPropagation()
        onOpenPreview?.()
      }}>
      {index !== undefined && showFileCount
        ? `${files.length} files selected`
        : localName}
    </button>
  )
}

export function ContentTableRowSkeleton() {
  return (
    <Table.Row>
      <Table.Data padding="compact" align="center">
        <div className="flex items-center justify-center px-4">
          <div className="h-4 w-4 animate-pulse rounded bg-gray-200" />
        </div>
      </Table.Data>
      <Table.Data padding="compact">
        <div className="flex min-h-[52px] w-full max-w-[260px] items-center space-x-3">
          <div className="flex items-center">
            <div className="h-[41px] w-[32px] animate-pulse rounded bg-gray-200" />
          </div>
          <div className="flex flex-col space-y-1">
            <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="ml-auto flex items-center">
            <div className="h-3.5 w-3.5 text-gray-300">
              <Spinner />
            </div>
          </div>
        </div>
      </Table.Data>
      <Table.Data padding="compact">
        <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
      </Table.Data>
      <Table.Data padding="compact">
        <div className="h-6 w-16 animate-pulse rounded bg-gray-200" />
      </Table.Data>
      <Table.Data padding="compact">
        <div className="h-6 w-6 animate-pulse rounded bg-gray-200" />
      </Table.Data>
    </Table.Row>
  )
}
